'use client';

import React, { useState, useEffect, useRef } from 'react';
import { 
  Lock, 
  Menu, 
  X, 
  ChevronDown, 
  ChevronRight, 
  ArrowUpRight,
  Calculator,
  Building2,
  FileCheck,
  Users,
  ShieldCheck,
  Compass,
  Layers,
  FileText,
  HelpCircle,
  TrendingUp,
  Sparkles
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { PremiumBackground } from '../effects/PremiumBackground';

interface NavSection {
  title: string;
  category: string;
  items: {
    id: string;
    href: string;
    label: string;
    tag?: string;
    desc?: string;
    icon?: React.ElementType;
  }[];
}

const NAV_CATALOG: NavSection[] = [
  {
    category: 'ОЦЕНКА & РЫНОК',
    title: 'Аналитика актива',
    items: [
      {
        id: 'valuation',
        href: '#valuation-matrix',
        label: 'Калькулятор 7.5x',
        tag: 'EBITDA',
        desc: 'Симулятор мультипликатора и дельты капитализации',
        icon: Calculator
      },
      {
        id: 'market',
        href: '#market-reality',
        label: 'Рыночный шок 2026',
        tag: 'Тренды',
        desc: 'Фактические сделки, дисконты и аппетиты стратегов',
        icon: TrendingUp
      },
      {
        id: 'case-study',
        href: '#case-study',
        label: 'Кейс Остоженки',
        tag: 'Срыв сделки',
        desc: 'Анатомия потери 140 млн ₽ из-за скрытых дефектов',
        icon: Building2
      },
    ]
  },
  {
    category: 'САНАЦИЯ & ДИЗАЙН',
    title: 'Подготовка к сделке',
    items: [
      {
        id: 'approach',
        href: '#approach',
        label: 'Deal Design S&P',
        tag: 'Методология',
        desc: 'Архитектура институциональной упаковки клиники',
        icon: Compass
      },
      {
        id: 'diagnostic',
        href: '#diagnostic',
        label: 'Диагностика QoE',
        tag: '12 метрик',
        desc: 'Экспресс-аудит 152-ФЗ, 54.1 НК РФ, БТИ и врачей',
        icon: FileCheck
      },
      {
        id: 'clinicos',
        href: '#clinicos',
        label: 'Clinic OS™',
        tag: 'Система',
        desc: 'Оцифровка процессов и независимость от собственника',
        icon: Layers
      },
      {
        id: 'exit-strategy',
        href: '#exit-strategy',
        label: 'Каскадный выход',
        tag: 'M&A Exit',
        desc: 'Стратегия продажи доли институциональным фондам',
        icon: ArrowUpRight
      },
    ]
  },
  {
    category: 'СИНДИКАТ & ЗАЩИТА',
    title: 'Инвесторы и безопасность',
    items: [
      {
        id: 'syndicate',
        href: '#syndicate',
        label: 'Синдикат M&A',
        tag: 'Инвесторы',
        desc: 'Закрытый пул аккредитованных медицинских холдингов',
        icon: Users
      },
      {
        id: 'precedents',
        href: '#precedents',
        label: 'Прецеденты сделок',
        tag: 'Томбстоуны',
        desc: 'Закрытые транзакции в частной медицине РФ',
        icon: ShieldCheck
      },
      {
        id: 'vault-docs',
        href: '#vault-docs',
        label: 'Knowledge Vault',
        tag: 'Меморандумы',
        desc: 'Шаблоны SPA, SHA, договоров задатка и NDA',
        icon: FileText
      },
      {
        id: 'faq',
        href: '#faq',
        label: 'FAQ & Инвесторы',
        tag: 'Вопросы',
        desc: 'Снятие паранойи: утечки, персонал, проверки',
        icon: HelpCircle
      },
    ]
  }
];

// Quick primary links for uncramped display on desktop
const PRIMARY_LINKS = [
  { href: '#valuation-matrix', label: 'Оценка 7.5x' },
  { href: '#case-study', label: 'Кейс Остоженки' },
  { href: '#diagnostic', label: 'Диагностика' },
  { href: '#syndicate', label: 'Синдикат' },
];

export const Header: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [catalogMenuOpen, setCatalogMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);

  const catalogDropdownRef = useRef<HTMLDivElement>(null);
  const catalogBtnRef = useRef<HTMLButtonElement>(null);

  // Close catalog flyout on click outside or escape
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        catalogDropdownRef.current && 
        !catalogDropdownRef.current.contains(event.target as Node) &&
        catalogBtnRef.current &&
        !catalogBtnRef.current.contains(event.target as Node)
      ) {
        setCatalogMenuOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setCatalogMenuOpen(false);
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Track scroll for progress line and dynamic header hide/show
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      
      if (docHeight > 0) {
        const progress = Math.min(100, Math.max(0, (currentScrollY / docHeight) * 100));
        setScrollProgress(progress);
      }

      if (currentScrollY > 100) {
        if (currentScrollY > lastScrollY && !mobileMenuOpen && !catalogMenuOpen) {
          setIsVisible(false);
        } else {
          setIsVisible(true);
        }
      } else {
        setIsVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY, mobileMenuOpen, catalogMenuOpen]);

  const handleOpenStressTest = () => {
    setCatalogMenuOpen(false);
    setMobileMenuOpen(false);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('sp_open_vault_handshake', { detail: { step: 1 } }));
    }
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-40 w-full transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${
        isVisible ? 'translate-y-0' : '-translate-y-full'
      }`}
    >
      {/* 2026 Scroll Progress: 2px золотой нитевидный индикатор */}
      <div className="w-full h-[2px] bg-[#EAE5DE] relative overflow-hidden">
        <div 
          className="h-full bg-[#8C6D37] transition-[width] duration-150 ease-out"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      {/* Textured Ivory Navigation Shell */}
      <div className="w-full relative overflow-visible bg-[#F6F3EC] border-b border-[#E3DDD2] shadow-[0_1px_0_rgba(255,255,255,0.9),0_6px_24px_-4px_rgba(70,55,30,0.07)]">
        <PremiumBackground variant={2} />

        {/* Мягкий световой градиент фактуры бумаги */}
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-white/60 via-transparent to-[#EDE7DC]/40" />

        <div className="relative z-10 max-w-[1560px] mx-auto px-3 sm:px-5 md:px-6 lg:px-8 h-[68px] sm:h-[72px] flex items-center justify-between gap-2 sm:gap-4">
          
          {/* Brand Identity: Compact, Luxury Debossed Gold Serif */}
          <a 
            href="#vault" 
            className="flex flex-col justify-center group flex-shrink-0 pr-3 sm:pr-4 lg:pr-5 border-r border-[#DDD7CC]/80"
            title="Структура & Партнеры — Институциональный M&A в частной медицине"
          >
            <span 
              className="text-[15px] sm:text-[16.5px] lg:text-[17.5px] font-serif text-[#8C6D37] group-hover:text-[#9A7B3E] transition-colors tracking-[0.12em] uppercase leading-none font-medium select-none whitespace-nowrap"
              style={{
                textShadow: '0 1.2px 0 rgba(255, 255, 255, 0.95), 0 -1px 1px rgba(70, 48, 14, 0.5), -0.5px 0 0.5px rgba(70, 48, 14, 0.3)'
              }}
            >
              СТРУКТУРА &amp; ПАРТНЕРЫ
            </span>
            <span className="text-[9px] sm:text-[9.5px] tracking-[0.12em] text-[#635F59] uppercase font-mono flex items-center gap-1.5 mt-1 select-none font-medium">
              <span>Healthcare M&amp;A</span>
              <span className="text-[#8C6D37]">•</span>
              <span className="text-[#8C6D37]">50–500 млн ₽</span>
            </span>
          </a>

          {/* Desktop Adaptive Navigation: 4 Core Quick-Links + "Все разделы" Menu */}
          <nav className="hidden lg:flex items-center gap-1 xl:gap-2 text-[12.5px] xl:text-[13px] text-[#4A453E] font-medium flex-1 justify-center min-w-0">
            {PRIMARY_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="px-2.5 xl:px-3 py-1.5 rounded-[2px] hover:text-[#141416] hover:bg-[#EAE4D8]/80 transition-colors whitespace-nowrap tracking-wide"
              >
                {link.label}
              </a>
            ))}

            {/* Smart Flyout Catalog Button */}
            <div className="relative">
              <button
                ref={catalogBtnRef}
                onClick={() => setCatalogMenuOpen(!catalogMenuOpen)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-[2px] transition-all text-xs tracking-wider uppercase font-medium border ${
                  catalogMenuOpen 
                    ? 'bg-[#E5DECة]/90 text-[#141416] border-[#8C6D37]/40 shadow-xs' 
                    : 'bg-[#EDE7DC]/60 hover:bg-[#EAE4D8] text-[#5C5853] hover:text-[#141416] border-[#DDD7CC]'
                }`}
                aria-expanded={catalogMenuOpen}
                aria-haspopup="true"
              >
                <Layers className="w-3.5 h-3.5 text-[#8C6D37]" />
                <span className="whitespace-nowrap">Все разделы</span>
                <ChevronDown className={`w-3.5 h-3.5 text-[#8C6D37] transition-transform duration-200 ${catalogMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Mega-Menu Flyout Dropdown */}
              <AnimatePresence>
                {catalogMenuOpen && (
                  <motion.div
                    ref={catalogDropdownRef}
                    initial={{ opacity: 0, y: 8, scale: 0.99 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 6, scale: 0.99 }}
                    transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[780px] bg-[#F7F4EE] border border-[#DDD7CC] rounded-[4px] shadow-[0_18px_40px_-8px_rgba(40,30,15,0.18),0_1px_0_rgba(255,255,255,0.9)_inset] p-5 z-50 text-left"
                  >
                    {/* Header of the catalog */}
                    <div className="flex items-center justify-between pb-3.5 mb-4 border-b border-[#E3DDD2]">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-[#8C6D37]" />
                        <span className="text-[11px] font-mono uppercase tracking-[0.16em] text-[#7A746B] font-semibold">
                          РЕЕСТР АНАЛИТИЧЕСКИХ МАТЕРИАЛОВ S&amp;P (12 ДОСЬЕ)
                        </span>
                      </div>
                      <span className="text-[10.5px] font-mono text-[#8C6D37] bg-[#EDE7DC] px-2 py-0.5 rounded-[2px] border border-[#DDD7CC]">
                        152-ФЗ Zero-Trace
                      </span>
                    </div>

                    {/* 3 Categories Grid */}
                    <div className="grid grid-cols-3 gap-5">
                      {NAV_CATALOG.map((cat, idx) => (
                        <div key={idx} className="space-y-3">
                          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#8C6D37] font-semibold pb-1 border-b border-[#E8E2D6]">
                            {cat.category}
                          </div>
                          <div className="space-y-1.5">
                            {cat.items.map((item) => {
                              const Icon = item.icon || FileText;
                              return (
                                <a
                                  key={item.id}
                                  href={item.href}
                                  onClick={() => setCatalogMenuOpen(false)}
                                  className="group flex flex-col p-2 rounded-[3px] hover:bg-[#EAE4D8]/80 transition-colors border border-transparent hover:border-[#DDD7CC]/80"
                                >
                                  <div className="flex items-center justify-between mb-0.5">
                                    <div className="flex items-center gap-1.5">
                                      <Icon className="w-3.5 h-3.5 text-[#8C6D37] group-hover:text-[#141416] transition-colors" />
                                      <span className="text-[12.5px] font-medium text-[#2A2724] group-hover:text-[#8C6D37] transition-colors">
                                        {item.label}
                                      </span>
                                    </div>
                                    {item.tag && (
                                      <span className="text-[9px] font-mono bg-[#EDE7DC] text-[#6B655D] px-1 py-0.2 rounded text-[8.5px]">
                                        {item.tag}
                                      </span>
                                    )}
                                  </div>
                                  {item.desc && (
                                    <p className="text-[10.5px] text-[#6E6860] line-clamp-1 leading-snug">
                                      {item.desc}
                                    </p>
                                  )}
                                </a>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Bottom Action Footer inside Flyout */}
                    <div className="mt-4 pt-3.5 border-t border-[#E3DDD2] flex items-center justify-between bg-[#EFE9DF]/60 -mx-5 -mb-5 p-4 rounded-b-[4px]">
                      <div className="text-[11px] text-[#5C5853] flex items-center gap-1.5">
                        <Sparkles className="w-3.5 h-3.5 text-[#8C6D37]" />
                        <span>Экспресс-оценка медицинского актива: дельта до <strong>+182 млн ₽</strong></span>
                      </div>
                      <button
                        onClick={handleOpenStressTest}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider bg-[#8C6D37] hover:bg-[#9A7B3E] text-white px-3.5 py-1.5 rounded-[2px] transition-colors shadow-2xs"
                      >
                        <span>Запустить стресс-тест</span>
                        <ChevronRight className="w-3.5 h-3.5 text-white/90" />
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </nav>

          {/* Right Action Block: Status Badge + Mutual NDA Gateway Button */}
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {/* Subtle Zero-Trace Status Pill (visible on xl screens) */}
            <div className="hidden 2xl:flex items-center gap-2 bg-[#EDE7DC]/70 border border-[#DDD7CC] px-2.5 py-1 rounded-[2px] text-[10.5px] font-mono text-[#5C5853]">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-600" />
              </span>
              <span>152-ФЗ Zero-Trace</span>
            </div>

            {/* Main Action Button */}
            <a
              href="#gateway"
              className="inline-flex items-center gap-1.5 sm:gap-2 bg-[#8C6D37] hover:bg-[#9A7B3E] text-white px-3.5 sm:px-4 lg:px-5 py-2 sm:py-2.5 rounded-[2px] text-[11px] sm:text-xs font-semibold tracking-wider uppercase transition-all shadow-[0_1px_2px_rgba(0,0,0,0.1),inset_0_1px_0_rgba(255,255,255,0.25)] border border-[#7A5E2E] whitespace-nowrap"
            >
              <Lock className="w-3.5 h-3.5 text-[#F6F3EC]" />
              <span className="hidden sm:inline">Шлюз Mutual NDA</span>
              <span className="sm:hidden">NDA</span>
              <ChevronRight className="w-3 h-3 text-[#F6F3EC] hidden sm:inline" />
            </a>

            {/* Mobile / Tablet Menu Trigger (for screens < lg) */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-[#5C5853] hover:text-[#141416] hover:bg-[#EAE4D8]/80 rounded-[2px] transition-colors focus:outline-none"
              aria-label="Toggle navigation menu"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile & Tablet Drawer */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.22, ease: 'easeInOut' }}
              className="lg:hidden overflow-hidden border-b border-[#DDD7CC] bg-[#F6F3EC] shadow-xl"
            >
              <div className="px-4 py-4 max-h-[82vh] overflow-y-auto space-y-4">
                
                {/* Fast Action in Mobile Menu */}
                <div className="p-3 bg-[#EFE9DF] border border-[#DDD7CC] rounded-[3px] flex items-center justify-between">
                  <div>
                    <div className="text-xs font-semibold text-[#141416]">Стресс-тест медицинского актива</div>
                    <div className="text-[10px] text-[#6B655D]">Оценка рисков по 54.1 НК РФ и БТИ</div>
                  </div>
                  <button
                    onClick={handleOpenStressTest}
                    className="text-[11px] font-semibold bg-[#8C6D37] text-white px-2.5 py-1.5 rounded-[2px] uppercase tracking-wider"
                  >
                    Тест
                  </button>
                </div>

                {/* Categorized Mobile Navigation */}
                <div className="space-y-4">
                  {NAV_CATALOG.map((cat, idx) => (
                    <div key={idx} className="space-y-1.5">
                      <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-[#8C6D37] font-semibold px-1">
                        {cat.category}
                      </div>
                      <div className="grid grid-cols-1 gap-1">
                        {cat.items.map((item) => {
                          const Icon = item.icon || FileText;
                          return (
                            <a
                              key={item.id}
                              href={item.href}
                              onClick={() => setMobileMenuOpen(false)}
                              className="flex items-center justify-between p-2 rounded-[2px] bg-[#EDE7DC]/40 hover:bg-[#EAE4D8] border border-[#DDD7CC]/60 text-xs font-medium text-[#2A2724]"
                            >
                              <div className="flex items-center gap-2">
                                <Icon className="w-3.5 h-3.5 text-[#8C6D37]" />
                                <span>{item.label}</span>
                              </div>
                              {item.tag && (
                                <span className="text-[9px] font-mono text-[#6B655D] bg-[#E3DDD2] px-1.5 py-0.5 rounded">
                                  {item.tag}
                                </span>
                              )}
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Bottom Gateway Button */}
                <a
                  href="#gateway"
                  onClick={() => setMobileMenuOpen(false)}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-[#8C6D37] hover:bg-[#9A7B3E] text-white rounded-[2px] text-xs font-semibold uppercase tracking-wider shadow-sm"
                >
                  <Lock className="w-3.5 h-3.5 text-white" />
                  <span>Шлюз Mutual NDA (Защищенный контур)</span>
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};
